import React, { PropsWithChildren } from "react";

// Wraps a route's `component` in one or more context providers, without introducing a new
// ancestor <Route> above it. `component` is already a distinct type per route -- PrivateRoute
// renders <Layout><Component .../></Layout>, and Component already fully remounts on every
// navigation to a different route regardless of provider scoping. Providers inserted here add
// zero new remount surface anywhere: Layout/Sidebar/Searchbar/MenuItem never see a type change at
// their position, since this only changes what's rendered *below* Component, not above it.
//
// (A nested <Route element={<SomeLayout/>}> wrapping several leaf routes was tried instead and
// reverted -- it changes the type sitting directly above Layout for the wrapped routes vs. every
// other route, so React tears down and rebuilds Layout/Sidebar/Searchbar/MenuItem on every
// navigation across that boundary. See okf/templates/e2e.md and the workspace-bootstrap loader
// fix for the same bug shape hitting Searchbar's old bootstrap ref.)
//
// Trade-off, accepted: each leaf route gets its own fresh provider instance rather than one
// shared across a cluster of routes, so state doesn't survive navigating between them (e.g.
// ArsContext's generate/promote job-polling state resets if the user navigates away mid-poll --
// the completion toast still fires independently, only the in-progress indicator resets).
export function withProviders(
    Component: React.ComponentType<any>,
    providers: React.ComponentType<PropsWithChildren>[]
) {
    const Wrapped = (props: any) =>
        providers.reduceRight(
            (children, Provider) => <Provider>{children}</Provider>,
            <Component {...props} />
        );
    Wrapped.displayName = `withProviders(${Component.displayName || Component.name || "Component"})`;
    return Wrapped;
}
